/**
 * Стадия корня приложения — один источник и для ветвления рендера, и для
 * защёлки «приложение уже показывали».
 *
 * Защёлка раньше выписывала условия рендера ЗАНОВО: тот же набор гейтов
 * второй раз, строчкой ниже. Это не сокращение записи, а два места, которые
 * обязаны совпадать и ничем друг с другом не связаны. Любой новый гейт — ещё
 * один `Navigate`, ещё один экран ошибки — попал бы в цепочку и не попал в
 * защёлку; человек, отскочивший в онбординг, вернулся бы с уже взведённой
 * защёлкой, и первый настоящий показ приложения прошёл бы мимо кадра ожидания.
 *
 * Отдельный модуль, а не функция в `routes/__root.tsx`: файлы в `src/routes`
 * разбирает генератор маршрутов, а импорт самого `__root.tsx` из теста тянет
 * за собой всё дерево приложения.
 */
export type RootStage =
  | 'waiting'
  | 'signIn'
  | 'roleError'
  | 'linkPortal'
  | 'onboarding'
  | 'app';

export function resolveRootStage({
  isClerkLoaded,
  isRoleLoading,
  isSignedIn,
  hasRoleError,
  orgCount,
  hasClaimTicket,
  isConsentSettled,
  hasShownApp,
}: {
  isClerkLoaded: boolean;
  isRoleLoading: boolean;
  isSignedIn: boolean;
  hasRoleError: boolean;
  orgCount: number;
  /** Есть ли след claim-билета: человек пришёл из CRM-портала. */
  hasClaimTicket: boolean;
  isConsentSettled: boolean;
  /** Показывали ли приложение хоть раз за эту загрузку страницы. */
  hasShownApp: boolean;
}): RootStage {
  if (!isClerkLoaded || isRoleLoading) return 'waiting';
  if (!isSignedIn) return 'signIn';
  if (hasRoleError) return 'roleError';

  // Нет организаций — обычно это новая регистрация, и человеку нужен
  // онбординг. Но след claim-билета означает, что он пришёл из CRM-портала,
  // где организация у него УЖЕ есть: молча завести ему вторую (со своим
  // демо-посевом и второй личностью в дашбордах) — ровно тот исход, ради
  // которого весь мост и делался. `/link-portal` разберётся, жив билет или
  // протух, и в любом случае объяснит, что происходит.
  if (orgCount === 0) return hasClaimTicket ? 'linkPortal' : 'onboarding';

  // Кадр ожидания второй раз — но ТОЛЬКО до первого показа приложения.
  //
  // `useConsentStatus` ключуется на `selectedOrgId`, поэтому при смене
  // организации запрос уходит заново и статус снова «в полёте». Вернуть отсюда
  // кадр в этот момент значило бы подменить корневой элемент, унося ВСЁ дерево
  // провайдеров. Защёлка оставляет кадр ожидания холодному старту.
  //
  // Чего она НЕ чинит: на смене организации `ConsentGate` этажом ниже вернёт
  // свой `AppLayoutSkeleton` и снесёт поддерево вместе с `LoadingOverlay`,
  // который живёт внутри него же. Это отдельный, более старый дефект
  // переключения организаций — #712.
  if (!hasShownApp && !isConsentSettled) return 'waiting';

  return 'app';
}
